import 'aws-amplify/auth/enable-oauth-listener';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Loading } from '@/components/Loading';
import { getSelfUserInfo } from './api';
import { authState, onAuthResolvedAction } from './state';

// 公開ルートは遅延ロードの子が Suspense で止まり ResolveAuth の effect が遅れるため、
// OAuth 完了時の `customOAuthState` を取りこぼさないようモジュール読み込み時から購読する。
let pendingRedirectPath: string | null = null;
Hub.listen('auth', ({ payload }) => {
  if (payload.event === 'customOAuthState' && payload.data) {
    pendingRedirectPath = payload.data;
  }
});

const getUserData = () =>
  getSelfUserInfo().then((user) => {
    return {
      id: user.userId,
      name: user.name ?? '',
      picture: user.picture ?? '',
    };
  });

export const ResolveAuth = (props: { required: boolean }) => {
  const navigate = useNavigate();
  const { isAuthResolved, user } = useAtomValue(authState);
  const onAuthResolved = useSetAtom(onAuthResolvedAction);

  useEffect(() => {
    let canceled = false;
    let done = false;

    const finish = (fn: () => void) => {
      if (!done && !canceled) {
        done = true;
        fn();
      }
    };

    const off = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signedIn': {
          finish(async () => {
            const user = await getUserData();
            onAuthResolved(user);
          });
          break;
        }
        case 'signInWithRedirect_failure': {
          finish(() => {
            onAuthResolved(null);
            navigate('/login', { replace: true });
          });
          break;
        }
        case 'signedOut': {
          if (!canceled) {
            onAuthResolved(null);
            navigate('/login', { replace: true });
          }
          break;
        }
      }
    });

    const withTimeout = <T,>(p: Promise<T>, ms = 5000) =>
      Promise.race([
        p,
        new Promise<never>((_, r) =>
          setTimeout(() => r(new Error('auth timeout')), ms),
        ),
      ]);

    (async () => {
      try {
        const { tokens } = await withTimeout(fetchAuthSession(), 5000);
        if (tokens?.idToken) {
          finish(async () => {
            const user = await getUserData();
            onAuthResolved(user);
          });
        } else {
          throw new Error('no session');
        }
      } catch {
        if (props.required) {
          finish(() => {
            onAuthResolved(null);
            const to = window.location.pathname;
            navigate(`/login?to=${encodeURIComponent(to)}`, { replace: true });
          });
          return;
        }
        // 公開ルートでは未ログインを確定させるだけで解決済みにはしない。
        // この画面からログインしたときに、あとから届く `signedIn` を通すため。
        if (!canceled) onAuthResolved(null);
      }
    })();

    return () => {
      canceled = true;
      off();
    };
  }, [onAuthResolved, navigate, props.required]);

  useEffect(() => {
    if (!user || pendingRedirectPath === null) return;
    const to = pendingRedirectPath;
    pendingRedirectPath = null;
    navigate(to);
  }, [user, navigate]);

  if (props.required && !isAuthResolved) {
    return <Loading />;
  }

  return <Outlet />;
};
